/**
 * BLU-1506 "Order and wait": the Order Queue's Waiting on blank view, the
 * plain parts (URL contract, grouping, status words). Components live in
 * `components/WaitingOnBlankList.tsx` and `components/WaitingOnBlankButton.tsx`.
 *
 * The server pages ORDERS (filter, count and page in one SQL layer); this file
 * only arranges the rows of the page on screen: by PO, then by blank, then the
 * orders that wait for that blank on that PO. Nothing here filters or counts
 * across pages.
 */
import type { OrderAndWaitBlank, OrderAndWaitOrderRow, OrderAndWaitPo } from '@threadheads/shared';
import { getSizeOrder } from '@threadheads/shared';
import { blankStyleName } from '@/lib/blank-style-name';

// ── URL contract ─────────────────────────────────────────────────────────────

/** `/production/orders?view=waiting_blank` opens the view (other surfaces link here). */
export const QUEUE_VIEW_PARAM = 'view';
export const WAITING_BLANK_VIEW = 'waiting_blank';
/** The view's own search box (never the queue's `search`, which belongs to the queue list). */
export const WAITING_SEARCH_PARAM = 'waiting_search';

/**
 * A link into the view. `search` matches an order name, a blank SKU or a PO
 * number on the server, so a PO page can narrow the view to its own orders by
 * passing its PO number.
 */
export function waitingOnBlankHref(search?: string): string {
  const params = new URLSearchParams({ [QUEUE_VIEW_PARAM]: WAITING_BLANK_VIEW });
  const trimmed = search?.trim();
  if (trimmed) params.set(WAITING_SEARCH_PARAM, trimmed);
  return `/production/orders?${params.toString()}`;
}

// ── Statuses ─────────────────────────────────────────────────────────────────

/** The wait statuses a waiting row can show, worst first (the SQL's rank). */
export const WAITING_STATUSES = ['out', 'short', 'late', 'not_sent', 'on_the_way'] as const;
export type WaitingStatus = (typeof WAITING_STATUSES)[number];

const RANK: Record<WaitingStatus, number> = { out: 0, short: 1, late: 2, not_sent: 3, on_the_way: 4 };

/** An unknown status from the wire reads as the calmest one, never as a problem. */
export function asWaitingStatus(status: string | null | undefined): WaitingStatus {
  return (WAITING_STATUSES as readonly string[]).includes(status ?? '') ? (status as WaitingStatus) : 'on_the_way';
}

export function worseStatus(a: WaitingStatus, b: WaitingStatus): WaitingStatus {
  return RANK[a] <= RANK[b] ? a : b;
}

/**
 * The PO header's status: a PO is never out or short, its rows are. Words and
 * tones for every status come from the PO feature's `OrderAndWaitStatusChip`
 * (one vocabulary on To buy, the PO page and this view).
 */
export type WaitingPoStatus = 'late' | 'on_the_way' | 'not_sent';

// ── Grouping ─────────────────────────────────────────────────────────────────

export type WaitingOrderEntry = {
  row: OrderAndWaitOrderRow;
  /** Units of this blank the order waits for on this PO. */
  qty: number;
  /** The worst status of the order's rows for this blank on this PO. */
  status: WaitingStatus;
  arrivalEtaDate: string | null;
};

export type WaitingBlankGroup = {
  key: string;
  skuId: string | null;
  sku: string | null;
  title: string | null;
  colour: string | null;
  size: string | null;
  orders: WaitingOrderEntry[];
};

export type WaitingPoGroup = {
  poId: string;
  poNumber: string;
  /** The supplier name from the PO header, else from the rows. */
  supplierName: string | null;
  status: WaitingPoStatus;
  /** The earliest due date among the page's rows on this PO. */
  arrivalEtaDate: string | null;
  blanks: WaitingBlankGroup[];
};

const blankKey = (b: OrderAndWaitBlank): string => b.skuId ?? b.sku ?? `${b.title}|${b.colour}|${b.size}`;

const minDate = (a: string | null, b: string | null): string | null => {
  if (!a) return b;
  if (!b) return a;
  return a <= b ? a : b;
};

/**
 * The PO's status from its header (the To buy read) when we have it, else from
 * its rows: a building PO is Not sent yet, a sent one is Late once any of its
 * rows is late, else On the way.
 */
export function poStatusOf(
  poStatus: string | null,
  rowStatuses: readonly WaitingStatus[],
  header?: Pick<OrderAndWaitPo, 'displayStatus'> | null,
): WaitingPoStatus {
  const fromHeader = header?.displayStatus;
  if (fromHeader === 'late' || fromHeader === 'on_the_way' || fromHeader === 'not_sent') return fromHeader;
  if (poStatus === 'draft') return 'not_sent';
  return rowStatuses.includes('late') ? 'late' : 'on_the_way';
}

const PO_ORDER: Record<WaitingPoStatus, number> = { late: 0, on_the_way: 1, not_sent: 2 };

const compareText = (a: string | null, b: string | null): number => {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a.localeCompare(b);
};

/** The style without its own size and colour ("Staple Tee M Black" -> "Staple Tee"). */
const styleOf = (b: WaitingBlankGroup): string | null =>
  b.title ? blankStyleName(b.title, b.colour, b.size) : null;

/** Style, then colour, then size in garment order (S, M, L, XL), then SKU. */
const compareBlank = (a: WaitingBlankGroup, b: WaitingBlankGroup): number =>
  compareText(styleOf(a), styleOf(b)) ||
  compareText(a.colour, b.colour) ||
  (a.size && b.size ? getSizeOrder(a.size) - getSizeOrder(b.size) : 0) ||
  compareText(a.size, b.size) ||
  compareText(a.sku, b.sku);

/**
 * The page's orders arranged by PO, then blank. An order that waits for two
 * blanks is listed under each; an order with two rows for one blank on one PO
 * (one of them flagged out) is listed once, with the units summed and the
 * worse status. Only waiting rows count. POs run late first, then on the way
 * (soonest due first), then the one still being built; orders keep the page's
 * order (oldest first, the server's sort).
 */
export function groupWaitingRows(
  rows: readonly OrderAndWaitOrderRow[],
  headers: ReadonlyMap<string, OrderAndWaitPo> = new Map(),
): WaitingPoGroup[] {
  type PoAcc = {
    poId: string;
    poNumber: string;
    poStatus: string | null;
    supplierName: string | null;
    arrivalEtaDate: string | null;
    statuses: WaitingStatus[];
    blanks: Map<string, WaitingBlankGroup>;
    /** One entry per (blank, order): `${blankKey} NUL ${prodOrderId}`. */
    entries: Map<string, WaitingOrderEntry>;
  };
  const pos = new Map<string, PoAcc>();

  for (const row of rows) {
    for (const blank of row.blanks) {
      if (blank.state !== 'waiting' || !blank.poId || blank.qty <= 0) continue;
      const status = asWaitingStatus(blank.status);
      let po = pos.get(blank.poId);
      if (!po) {
        po = {
          poId: blank.poId,
          poNumber: blank.poNumber ?? '',
          poStatus: blank.poStatus,
          supplierName: blank.supplierName,
          arrivalEtaDate: null,
          statuses: [],
          blanks: new Map(),
          entries: new Map(),
        };
        pos.set(blank.poId, po);
      }
      po.statuses.push(status);
      po.arrivalEtaDate = minDate(po.arrivalEtaDate, blank.arrivalEtaDate);

      const key = blankKey(blank);
      let group = po.blanks.get(key);
      if (!group) {
        group = {
          key,
          skuId: blank.skuId,
          sku: blank.sku,
          title: blank.title,
          colour: blank.colour,
          size: blank.size,
          orders: [],
        };
        po.blanks.set(key, group);
      }

      const entryKey = `${key}\u0000${row.prodOrderId}`;
      const existing = po.entries.get(entryKey);
      if (existing) {
        existing.qty += blank.qty;
        existing.status = worseStatus(existing.status, status);
        existing.arrivalEtaDate = minDate(existing.arrivalEtaDate, blank.arrivalEtaDate);
      } else {
        const entry: WaitingOrderEntry = { row, qty: blank.qty, status, arrivalEtaDate: blank.arrivalEtaDate };
        po.entries.set(entryKey, entry);
        group.orders.push(entry);
      }
    }
  }

  const groups: WaitingPoGroup[] = Array.from(pos.values()).map((po) => {
    const header = headers.get(po.poId) ?? null;
    return {
      poId: po.poId,
      poNumber: header?.poNumber || po.poNumber,
      supplierName: header?.supplierName?.trim() || po.supplierName?.trim() || null,
      status: poStatusOf(header?.status ?? po.poStatus, po.statuses, header),
      arrivalEtaDate: header?.arrivalEtaDate ?? po.arrivalEtaDate,
      blanks: Array.from(po.blanks.values()).sort(compareBlank),
    };
  });

  return groups.sort(
    (a, b) =>
      PO_ORDER[a.status] - PO_ORDER[b.status] ||
      compareText(a.arrivalEtaDate, b.arrivalEtaDate) ||
      a.poNumber.localeCompare(b.poNumber),
  );
}
